import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { IApplicationModel } from 'interfaces';
import { getDefectAct } from '../api';
import { DefectAct } from '../interfaces';
import { EDefectActStatuses, hasStatuses } from '../helpers';
import { ECriteriaRatingUgoType } from '../components/Calculation/interfaces';

interface DefectActProviderProps {
  application: IApplicationModel;
  defectAct: DefectAct.IDefectAct;
  operations: string[];
  children: ReactNode;
}

interface DefectActContextValue extends Omit<DefectActProviderProps, 'children'> {
  $assignToTechDep: (defectAct: DefectAct.IDefectAct) => void;
  $blockDefectAct: () => void;
  $changeIsAgreedDefectAct: () => void;
  $closeDefectAct: () => void;
  $completeDefectAct: () => void;
  $createNotification: (notification: DefectAct.INotification) => void;
  $createPreNotification: (preNotification: DefectAct.IPreNotification) => void;
  $deleteDocument: (document: DefectAct.IDocument) => void;
  $deleteNotification: (notification: DefectAct.INotification) => void;
  $ratePartner: (type: ECriteriaRatingUgoType) => void;
  $reloadDefectAct: () => Promise<DefectAct.IDefectAct>;
  $resetPartnerRating: (type: ECriteriaRatingUgoType) => void;
  $rejectDefectAct: () => void;
  $sendNotification: (notification: DefectAct.INotification, sendInfo: DefectAct.INotificationSendInfo) => void;
  $sendNotificationInfo: (result: DefectAct.IResult) => void;
  $sendPreNotification: (preNotification: DefectAct.IPreNotification) => void;
  $sendToOneSystem: (info: DefectAct.ISendToOneSystemInfo) => void;
  $sendToPTO: () => void;
  $submitContragentBad: (notification: DefectAct.INotification, contragentBad: boolean) => void;
  $submitPreContragentBad: (preNotification: DefectAct.IPreNotification, contragentBad: boolean) => void;
  $submitMembers: (members: DefectAct.IMember[]) => void;
  $submitMembersToPartners: () => void;
  $unlockDefectAct: () => void;
  $updateDefectAct: (defectAct: DefectAct.IDefectAct) => void;
  $updateDetails: (details: DefectAct.ICalculation) => void;
  $updateFiles: (defectActFiles: DefectAct.IFile[]) => void;
  $updateNotification: (notification: DefectAct.INotification) => void;
  $updatePreNotification: (preNotification: DefectAct.IPreNotification) => void;
  $uploadDocuments: (documents: DefectAct.IDocument[]) => void;
}

const DefectActContext = createContext<DefectActContextValue | undefined>(undefined);

export const DefectActProvider: React.FC<DefectActProviderProps> = ({
  application,
  operations,
  defectAct: _defectAct,
  children,
}) => {
  // Local state
  const [defectAct, setDefectAct] = useState<DefectAct.IDefectAct>(_defectAct);

  // Методы (переименованы и отсортированы в алфавитном порядке)
  const $assignToTechDep = ({ considerationStatusId, considerationStatusName }: DefectAct.IDefectAct) => {
    setDefectAct((defectAct) => ({ ...defectAct, considerationStatusId, considerationStatusName }));
  };

  const $blockDefectAct = () => {
    const defectActStatusId = EDefectActStatuses['Заблокирован'];
    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $changeIsAgreedDefectAct = () => {
    setDefectAct((defectAct) => ({ ...defectAct, isAgreed: true }));
  };

  const $closeDefectAct = () => {
    const defectActStatusId = EDefectActStatuses['Закрыт без ресурсов'];
    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $completeDefectAct = () => {
    const defectActStatusId = EDefectActStatuses['Утверждено'];
    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $createNotification = (notification: DefectAct.INotification) => {
    const defectActStatusId = EDefectActStatuses['Создано уведомление'];
    setDefectAct((defectAct) => ({
      ...defectAct,
      notifications: [notification, ...defectAct.notifications!],
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $createPreNotification = (preNotification: DefectAct.IPreNotification) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      version: 2,
      preNotifications: [preNotification, ...(defectAct.preNotifications ?? [])],
    }));
  };

  const $updatePreNotification = (preNotification: DefectAct.IPreNotification) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      preNotifications: (defectAct.preNotifications ?? []).map((item) =>
        item.contragentGuid === preNotification.contragentGuid ? preNotification : item,
      ),
    }));
  };

  const $sendPreNotification = (preNotification: DefectAct.IPreNotification) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      preNotifications: (defectAct.preNotifications ?? []).map((item) =>
        item.contragentGuid === preNotification.contragentGuid ? preNotification : item,
      ),
    }));
  };

  const $deleteDocument = (document: DefectAct.IDocument) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      documents: defectAct.documents!.filter(
        ({ defectActDocumentId }) => defectActDocumentId !== document.defectActDocumentId,
      ),
    }));
  };

  const $deleteNotification = (notification: DefectAct.INotification) => {
    const defectActStatusId = EDefectActStatuses['Уведомление отправлено'];
    const notifications = defectAct.notifications!.filter(
      ({ contragentGuid }) => contragentGuid !== notification.contragentGuid,
    );
    const allNotificationHasSent = notifications.every((notif) => notif.sendInfo);
    setDefectAct((defectAct) => ({
      ...defectAct,
      notifications,
      ...(allNotificationHasSent
        ? { defectActStatusId, defectActStatusName: EDefectActStatuses[defectActStatusId] }
        : {}),
    }));
  };

  const $ratePartner = (criteriaType: ECriteriaRatingUgoType) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      ...(criteriaType === ECriteriaRatingUgoType.SECONDARY
        ? { secondStepRatingUgo: true }
        : { primaryStepRatingUgo: true }),
    }));
  };

  const $reloadDefectAct = async () => {
    const updatedDefectAct = await getDefectAct(application.applications[0].applicationNumber);
    setDefectAct(updatedDefectAct);
    return updatedDefectAct;
  };

  const $resetPartnerRating = (criteriaType: ECriteriaRatingUgoType) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      ...(criteriaType === ECriteriaRatingUgoType.SECONDARY
        ? { secondStepRatingUgo: false }
        : { primaryStepRatingUgo: false }),
    }));
  };

  const $rejectDefectAct = () => {
    const defectActStatusId = EDefectActStatuses['Отклонено'];
    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $sendNotification = (notification: DefectAct.INotification, sendInfo: DefectAct.INotificationSendInfo) => {
    const defectActStatusId = EDefectActStatuses['Уведомление отправлено'];
    const notifications = defectAct.notifications!.map((_notification) => {
      if (_notification.contragentGuid === notification.contragentGuid) _notification = { ..._notification, sendInfo };
      return _notification;
    });

    setDefectAct((defectAct) => ({
      ...defectAct,
      notifications,
      ...(notifications.every(({ sendInfo }) => Boolean(sendInfo))
        ? { defectActStatusId, defectActStatusName: EDefectActStatuses[defectActStatusId] }
        : {}),
    }));
  };

  const $sendNotificationInfo = (result: DefectAct.IResult) => {
    const defectActStatusId =
      EDefectActStatuses[result.fixByContractor ? 'Устраняется подрядчиком' : 'Требует калькуляции'];
    const defectActStatus = { defectActStatusId, defectActStatusName: EDefectActStatuses[defectActStatusId] };

    setDefectAct((defectAct) => ({
      ...defectAct,
      result,
      // Обновляем статус если: ДА ещё в «стартовых» статусах уведомления,
      // либо это первый result, либо выбрано «Устраняется подрядчиком».
      // Иначе (повторное сохранение result после калькуляции) — статус не меняем.
      ...(!defectAct.result ||
      result.fixByContractor ||
      hasStatuses(['Создано уведомление', 'Уведомление отправлено', 'Уведомление обновлено'], defectAct)
        ? defectActStatus
        : {}),
    }));
  };

  const $sendToOneSystem = (sendToOneSystemInfo: DefectAct.ISendToOneSystemInfo) => {
    const defectActStatusId = EDefectActStatuses['Отправлен в 1с'];

    setDefectAct((defectAct) => ({
      ...defectAct,
      sendToOneSystemInfo,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $sendToPTO = () => {
    const defectActStatusId = EDefectActStatuses['Отправлено в ПТО'];

    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $submitContragentBad = (notification: DefectAct.INotification, contragentBad: boolean) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      notifications: defectAct.notifications!.map((_notification) => {
        if (_notification.contragentGuid === notification.contragentGuid)
          _notification = { ...notification, contragentBad };
        return _notification;
      }),
    }));
  };

  const $submitPreContragentBad = (preNotification: DefectAct.IPreNotification, contragentBad: boolean) => {
    setDefectAct((defectAct) => ({
      ...defectAct,
      preNotifications: (defectAct.preNotifications ?? []).map((_preNotification) =>
        _preNotification.contragentGuid === preNotification.contragentGuid
          ? { ...preNotification, contragentBad }
          : _preNotification,
      ),
    }));
  };

  const $submitMembers = (members: DefectAct.IMember[]) => {
    setDefectAct((defectAct) => ({ ...defectAct, members }));
  };

  const $submitMembersToPartners = () => {
    const defectActStatusId = EDefectActStatuses['На подписании'];

    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $unlockDefectAct = () => {
    const defectActStatusId = EDefectActStatuses['На подписании'];

    setDefectAct((defectAct) => ({
      ...defectAct,
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $updateDefectAct = (defectActData: DefectAct.IDefectAct) => {
    setDefectAct(defectActData);
  };

  const $updateDetails = (calculation: DefectAct.ICalculation) => {
    const ON_CALCULATION = hasStatuses(['Требует калькуляции', 'На калькуляции'], defectAct);
    const IN_PTO = hasStatuses(['Отправлено в ПТО', 'Посчитан ПТО'], defectAct);
    const PTO_DONE = hasStatuses(
      [
        'На подписании',
        'Отправлен в 1с',
        'Дополнен',
        'Дефектный акт дополнен и распечатан',
        'Дефектный акт распечатан',
      ],
      defectAct,
    );

    const defectActStatusIdInPTO = IN_PTO ? EDefectActStatuses['Посчитан ПТО'] : EDefectActStatuses['Отправлено в ПТО'];
    const defectActStatusOnCalculation = ON_CALCULATION ? EDefectActStatuses['На калькуляции'] : defectActStatusIdInPTO;

    const defectActStatusId = PTO_DONE ? EDefectActStatuses['Дополнен'] : defectActStatusOnCalculation;

    setDefectAct((defectAct) => ({
      ...defectAct,

      ...(IN_PTO || PTO_DONE ? { pto: calculation } : { work: calculation }),

      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $updateFiles = (defectActFiles: DefectAct.IFile[]) => {
    setDefectAct((defectAct) => ({ ...defectAct, defectActFiles }));
  };

  const $updateNotification = (notification: DefectAct.INotification) => {
    const defectActStatusId = EDefectActStatuses['Уведомление обновлено'];

    setDefectAct((defectAct) => ({
      ...defectAct,
      notifications: defectAct.notifications!.map((_notification) => {
        if (_notification.contragentGuid === notification.contragentGuid) _notification = notification;
        return _notification;
      }),
      defectActStatusId,
      defectActStatusName: EDefectActStatuses[defectActStatusId],
    }));
  };

  const $uploadDocuments = (documents: DefectAct.IDocument[]) => {
    setDefectAct((defectAct) => ({ ...defectAct, documents }));
  };

  const values = {
    application,
    defectAct,
    operations,

    $assignToTechDep,
    $blockDefectAct,
    $changeIsAgreedDefectAct,
    $closeDefectAct,
    $completeDefectAct,
    $createNotification,
    $createPreNotification,
    $deleteDocument,
    $deleteNotification,
    $ratePartner,
    $reloadDefectAct,
    $resetPartnerRating,
    $rejectDefectAct,
    $sendNotification,
    $sendNotificationInfo,
    $sendPreNotification,
    $sendToOneSystem,
    $sendToPTO,
    $submitContragentBad,
    $submitPreContragentBad,
    $submitMembers,
    $submitMembersToPartners,
    $unlockDefectAct,
    $updateDefectAct,
    $updateDetails,
    $updateFiles,
    $updateNotification,
    $updatePreNotification,
    $uploadDocuments,
  };

  const state = useMemo(() => values, [values]);

  return <DefectActContext.Provider value={state}>{children}</DefectActContext.Provider>;
};

export const useDefectAct = (): DefectActContextValue => {
  const context = useContext(DefectActContext);

  if (!context) {
    throw new Error('useDefectAct должен использоваться внутри DefectActProvider');
  }

  return context;
};
